/* Shopify's public, tokenless API. Cursor traversal uses a stable product ID order. */
(function (root) {
  'use strict';
  const variantFields = `id title sku barcode weight weightUnit requiresComponents
    selectedOptions{name value} price{amount currencyCode} unitPrice{amount currencyCode}
    unitPriceMeasurement{measuredType quantityUnit quantityValue referenceUnit referenceValue}
    availableForSale image{url(transform:{maxWidth:600})}`;
  const productsQuery = `query EsvGroupedProducts($query:String,$cursor:String,$country:CountryCode!,$language:LanguageCode!)
    @inContext(country:$country,language:$language){products(first:250,after:$cursor,sortKey:ID,query:$query){
      nodes{id title handle productType onlineStoreUrl requiresSellingPlan options{name}
        featuredImage{url(transform:{maxWidth:600})}
        variants(first:5){nodes{${variantFields}}pageInfo{hasNextPage endCursor}}}
      pageInfo{hasNextPage endCursor}}}`;
  const variantsQuery = `query EsvGroupedVariants($id:ID!,$cursor:String,$country:CountryCode!,$language:LanguageCode!)
    @inContext(country:$country,language:$language){product(id:$id){
      variants(first:50,after:$cursor){nodes{${variantFields}}pageInfo{hasNextPage endCursor}}}}`;

  function numericId(id, kind) {
    const match = new RegExp(`^gid://shopify/${kind}/(\\d+)$`).exec(id || '');
    if (!match || !Number.isSafeInteger(Number(match[1]))) throw new Error('Invalid Shopify identity');
    return Number(match[1]);
  }

  function validateConnection(connection, cursor) {
    if (!connection || !Array.isArray(connection.nodes) || typeof connection.pageInfo?.hasNextPage !== 'boolean') throw new Error('Incomplete product response');
    if (connection.pageInfo.hasNextPage && (!connection.nodes.length || !connection.pageInfo.endCursor || connection.pageInfo.endCursor === cursor)) throw new Error('Incomplete cursor traversal');
    return connection;
  }

  function amount(value, currency) {
    if (!value || value.currencyCode !== currency || !Number.isFinite(Number(value.amount)) || Number(value.amount) < 0) throw new Error('Invalid price or currency');
    return Math.round(Number(value.amount) * 100);
  }

  function convert(product, currency) {
    if (!/^[a-z0-9][a-z0-9_-]*$/.test(product.handle) || !product.variants.nodes.length) throw new Error('Incomplete product');
    const weights = { GRAMS: 1, KILOGRAMS: 1000, OUNCES: 28.349523125, POUNDS: 453.59237 };
    return { id: numericId(product.id, 'Product'), title: product.title, type: product.productType,
      url: '/products/' + product.handle, optionNames: product.options.map(option => option.name),
      requiresSellingPlan: product.requiresSellingPlan,
      variants: product.variants.nodes.map(variant => {
        if (typeof variant.availableForSale !== 'boolean' || typeof variant.requiresComponents !== 'boolean' ||
            !Array.isArray(variant.selectedOptions) || !Object.hasOwn(weights, variant.weightUnit)) throw new Error('Incomplete variant');
        return { id: numericId(variant.id, 'ProductVariant'), sku: variant.sku, barcode: variant.barcode,
          title: variant.title, options: variant.selectedOptions.map(option => option.value),
          weight: Math.round(variant.weight * weights[variant.weightUnit] * 1000) / 1000,
          requiresComponents: variant.requiresComponents, unitPrice: variant.unitPrice ? amount(variant.unitPrice, currency) : null,
          unitMeasurement: variant.unitPriceMeasurement, price: amount(variant.price, currency),
          available: variant.availableForSale, image: variant.image?.url || product.featuredImage?.url || null };
      }) };
  }

  async function loadProducts(request, { query, country, language, currency }, progress = () => {}) {
    const context = { country, language };
    const products = [], ids = new Set(), cursors = new Set(), variantIds = new Set();
    let cursor = null, lastId = 0;
    async function completeVariants(product) {
      let connection = validateConnection(product.variants, null), after = null;
      const seen = new Set();
      while (connection.pageInfo.hasNextPage) {
        after = connection.pageInfo.endCursor;
        if (seen.has(after)) throw new Error('Repeated variant cursor');
        seen.add(after);
        const data = await request(variantsQuery, { ...context, id: product.id, cursor: after });
        connection = validateConnection(data.product?.variants, after);
        product.variants.nodes.push(...connection.nodes);
        if (product.variants.nodes.length > 2048) throw new Error('Variant limit exceeded');
      }
    }
    do {
      const data = await request(productsQuery, { ...context, query: query || null, cursor });
      const connection = validateConnection(data.products, cursor);
      let next = 0;
      await Promise.all(Array.from({ length: Math.min(3, connection.nodes.length) }, async () => {
        while (next < connection.nodes.length) await completeVariants(connection.nodes[next++]);
      }));
      for (const product of connection.nodes) {
        const id = numericId(product.id, 'Product');
        if (ids.has(id) || id <= lastId) throw new Error('Product traversal is not unique and ordered');
        ids.add(id); lastId = id;
        if (ids.size > 25000) throw new Error('Product traversal limit exceeded');
        const row = convert(product, currency);
        for (const variant of row.variants) {
          if (variantIds.has(variant.id)) throw new Error('Duplicate variant');
          variantIds.add(variant.id);
        }
        if (product.onlineStoreUrl) products.push(row);
      }
      progress(products.length);
      if (!connection.pageInfo.hasNextPage) return products;
      cursor = connection.pageInfo.endCursor;
      if (cursors.has(cursor)) throw new Error('Repeated product cursor');
      cursors.add(cursor);
    } while (true);
  }

  async function loadCollectionIds(request, { handle, country, language }) {
    const document = `query EsvCollectionMembership($handle:String!,$cursor:String,$country:CountryCode!,$language:LanguageCode!)
      @inContext(country:$country,language:$language){collection(handle:$handle){products(first:250,after:$cursor,sortKey:ID){
        nodes{id}pageInfo{hasNextPage endCursor}}}}`;
    const ids = new Set(), cursors = new Set();
    let cursor = null, lastId = 0;
    do {
      const data = await request(document, { handle, cursor, country, language });
      const connection = validateConnection(data.collection?.products, cursor);
      for (const row of connection.nodes) {
        const id = numericId(row.id, 'Product');
        if (id <= lastId || ids.has(id)) throw new Error('Invalid collection traversal');
        ids.add(id); lastId = id;
        if (ids.size > 25000) throw new Error('Collection traversal limit exceeded');
      }
      if (!connection.pageInfo.hasNextPage) return ids;
      cursor = connection.pageInfo.endCursor;
      if (cursors.has(cursor)) throw new Error('Repeated collection cursor');
      cursors.add(cursor);
    } while (true);
  }

  const api = { loadProducts, loadCollectionIds, convert, validateConnection };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.EsvSearchStorefront = api;
})(typeof window === 'undefined' ? globalThis : window);
